import { Avatar } from '@evisort/eds/components/Avatar/Avatar';
import { Banner } from '@evisort/eds/components/Banner/Banner';
import { Card } from '@evisort/eds/components/Card/Card';
import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Modal } from '@evisort/eds/components/Modal/Modal';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  title: {
    id: 'CrossClientSwitchConfirmModal.title',
    defaultMessage: 'Switch to {toSubdomain}?',
    description:
      'Title for the cross-client workspace switch confirmation modal',
  },
  activeSession: {
    id: 'CrossClientSwitchConfirmModal.activeSession',
    defaultMessage:
      'You have an active session in {fromSubdomain}. Switching will sign you out and any unsaved work will be lost - you’ll need to sign in to {toSubdomain} to continue.',
    description:
      'Warning callout naming the active session and explaining the effect of switching tenants',
  },
  stayButton: {
    id: 'CrossClientSwitchConfirmModal.stayButton',
    defaultMessage: 'Stay on {fromSubdomain}',
    description:
      'Button that cancels the workspace switch and returns to the current session',
  },
  continueButton: {
    id: 'CrossClientSwitchConfirmModal.continueButton',
    defaultMessage: 'Continue to {toSubdomain}',
    description:
      'Button that confirms the workspace switch, signing out of the current session',
  },
});

interface CrossClientSwitchConfirmModalProps {
  fromSubdomain: string;
  toSubdomain: string;
  onCancel: () => void;
  onConfirm: () => void;
  userName?: string;
  userEmail?: string;
}

/**
 * Shown when the URL points at a different tenant than the one the user is
 * actively signed in to (e.g. typing a different workspace slug into the
 * address bar while already logged in). Switching tenants signs the user out
 * of the current one, so this asks for confirmation before that happens
 * instead of silently tearing down the session the moment the mismatch is
 * detected. Staying on the current tenant is the safer, non-destructive
 * choice, so it's styled as the primary action rather than continuing.
 */
function CrossClientSwitchConfirmModal({
  fromSubdomain,
  toSubdomain,
  onCancel,
  onConfirm,
  userName,
  userEmail,
}: CrossClientSwitchConfirmModalProps) {
  const intl = useIntl();

  const stayLabel = intl.formatMessage(messages.stayButton, {
    fromSubdomain,
  });
  const continueLabel = intl.formatMessage(messages.continueButton, {
    toSubdomain,
  });

  return (
    <Modal
      isVisible
      header={{ title: intl.formatMessage(messages.title, { toSubdomain }) }}
      footer={{
        actions: [
          {
            label: continueLabel,
            text: continueLabel,
            variant: 'tertiary',
            onClick: onConfirm,
          },
          {
            label: stayLabel,
            text: stayLabel,
            variant: 'primary',
            onClick: onCancel,
          },
        ],
      }}
      onHide={onCancel}
    >
      <Layout direction="column" spacing="m">
        <Banner
          status="cautious"
          text={intl.formatMessage(messages.activeSession, {
            fromSubdomain,
            toSubdomain,
          })}
        />
        {userName ? (
          <Card size="m">
            <Avatar mode="info" name={userName} secondaryText={userEmail} />
          </Card>
        ) : null}
      </Layout>
    </Modal>
  );
}

export default CrossClientSwitchConfirmModal;
